import * as R from 'ramda';
import { useMemo } from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
// mui
import Typography from '@mui/material/Typography';
// icons-material
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
// constants
import { FIELD_TYPE_TEXT } from '../../../../constants';
// utilities
import * as U from '../../../../utilities';
// ui
import { Box, Flex } from '../../../../ui';
// component edit-report
import FieldSelect from '../field-select';
import { withReportFieldList } from './hocs';
import { defaultReportField } from './settings';
//////////////////////////////////////////////////

const SortHeaderActionBtn = ({ theme, title, handleClick }) => (
  <Box
    ml={15}
    fontSize={13}
    cursor='pointer'
    fontWeight={500}
    hoveredOpacity={0.5}
    onClick={handleClick}
    color={theme.colors.success}
    title={U.getTranslation(`actions.${title}`, title)}
  >
    {U.getTranslation(`actions.${title}`, title)}
  </Box>
);

const SortHeader = props => {
  const {
    t,
    theme,
    maxFields,
    reportFieldList,
    setReportFieldList,
    defaultFieldsCount,
    handleAddReportField,
    handleAddReportFields,
    setDefaultFieldsCount,
  } = props;

  const count = R.compose(
    R.length,
    R.keys,
  )(reportFieldList);

  const inputStyles = {
    width: '22px',
    height: '22px',
    outline: 'none',
    fontSize: '12px',
    marginLeft: '15px',
    textAlign: 'center',
    borderRadius: '2px',
  };

  const handleChange = event => {
    const value = R.compose(
      R.when(
        parseInt,
        R.clamp(1, 50),
      ),
      R.replace(/[^\d]/g, ''),
      R.trim,
      R.defaultTo(''),
    )(U.getEventTargetValue(event));

    setDefaultFieldsCount(value);
  };

  return (
    <Flex
      p='0 15px'
      height={40}
      borderBottom='1px solid'
      bg={theme.colors.bgColor}
      justifyContent='space-between'
      color={theme.colors.lightBlack}
      borderColor={theme.colors.middleGrey}
    >
      <Typography>{U.getTranslation('labels.fields', 'Fields')}</Typography>
      {
        U.isNotNil(maxFields)
        && <span>{`${count}/${maxFields}`}</span>
      }
      <Flex ml={10} fontSize={12}>
        {
          R.gt(R.length(reportFieldList), 1)
          && (
            <SortHeaderActionBtn
              t={t}
              theme={theme}
              title='removeAll'
              handleClick={() => setReportFieldList(R.of(Array, defaultReportField))}
            />
          )
        }
        <SortHeaderActionBtn
          t={t}
          theme={theme}
          title='addFields'
          handleClick={handleAddReportFields}
        />
        <input
          type={FIELD_TYPE_TEXT}
          onChange={handleChange}
          value={defaultFieldsCount}
          style={{ ...inputStyles, border: `1px solid ${theme.colors.black}` }}
        />
        <SortHeaderActionBtn
          t={t}
          theme={theme}
          title='addField'
          handleClick={handleAddReportField}
        />
      </Flex>
    </Flex>
  );
};

const ItemAction = props => {
  const {
    theme,
    fieldIndex,
    handleRemoveReportField,
  } = props;

  return (
    <Flex>
      <Box
        ml={10}
        cursor='grab'
        alignItems='center'
        title={U.getTranslation('actions.dragAndDrop', 'Drag And Drop')}
      >
        <DragIndicatorIcon size='25px' sx={{ color: theme.colors.success }} />
      </Box>
      <Flex
        ml={10}
        cursor='pointer'
        alignItems='center'
        onClick={() => handleRemoveReportField(fieldIndex)}
        title={U.getTranslation('actions.remove', 'Remove')}
      >
        <DeleteIcon size='25px' sx={{ color: theme.colors.success }} />
      </Flex>
    </Flex>
  );
};

const FieldItem = props => {
  const {
    name,
    theme,
    zIndex,
    fieldIndex,
    availableFields,
    availableForReports,
    handleSelectReportFieldName,
  } = props;

  const field = R.or(availableFields, []).find(af => R.equals(name, af.value));

  let options = availableForReports;

  if (field) {
    options = R.concat([field], options);
  }

  return (
    <Flex py={10} alignItems='center' zIndex={zIndex} border='none'>
      <ItemAction {...props} />
      <FieldSelect
        value={name}
        theme={theme}
        options={options}
        fieldIndex={fieldIndex}
        id={`report-field-${fieldIndex}`}
        action={handleSelectReportFieldName}
      />
    </Flex>
  );
};

const Fields = props => {
  const {
    t,
    theme,
    dndProcessing,
    reportFieldList,
    availableFields,
    availableForReports,
    handleRemoveReportField,
    handleSelectReportFieldName,
  } = props;

  const itemBorderColor = theme.colors.middleGrey;
  const border = U.ifElse(U.isTrue(dndProcessing), `1px dashed ${itemBorderColor}`, 'none');

  const maxIndex = R.multiply(R.length(availableFields), 5);

  return useMemo(() => (
    <Flex border={border} flexDirection='column'>
      {
          reportFieldList.map((field, index) => (
            <Draggable
              key={index}
              index={index}
              draggableId={`report-field-list-dnd-item-${index}`}
            >
              {provided => (
                <Box
                  width='100%'
                  alignItems='start'
                  border='1px solid'
                  borderColor={itemBorderColor}
                  zIndex={R.subtract(maxIndex, R.inc(index))}
                >
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <FieldItem
                      {...field}
                      t={t}
                      theme={theme}
                      fieldIndex={index}
                      provided={provided}
                      availableFields={availableFields}
                      reportFieldList={reportFieldList}
                      availableForReports={availableForReports}
                      handleRemoveReportField={handleRemoveReportField}
                      handleSelectReportFieldName={handleSelectReportFieldName}
                      name={field.name ? U.getTranslation(`labels.${field.name}`, field.name) : ''}
                    />
                  </div>
                  {provided.placeholder}
                </Box>
              )}
            </Draggable>
          ))
        }
    </Flex>
  ), [dndProcessing, reportFieldList, availableFields]); // eslint-disable-line
};

const FieldList = props => {
  const { theme, reportFieldList } = props;

  const fieldsLength = R.keys(reportFieldList);

  return (
    <Box width='auto'>
      <SortHeader {...props} />
      <Box>
        {
          U.isNilOrEmpty(fieldsLength) && (
            <Box p={15}>
              <Flex flexDirection='column'>
                <Typography fontSize={16} color={theme.colors.darkGrey}>
                  {U.getTranslation('errors.youDontHaveAnyFieldsForThisReport', 'You don\'t have any fields for this report')}
                </Typography>
              </Flex>
            </Box>
          )
          }
      </Box>
      {
          U.isNotNilAndNotEmpty(fieldsLength)
          && (
          <Droppable droppableId='reportFields'>
            {(provided, snapshot) => (
              <div ref={provided.innerRef}>
                <Fields snapshot={snapshot} {...props} />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          )
        }
    </Box>
  );
};

export default withReportFieldList(FieldList);
